import { CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { AttachFile, Close, ErrorOutlined, InsertDriveFileOutlined } from "@mui/icons-material"
import { ChatComposerAttachButton, ChatComposerAttachmentList, ChatComposerTextArea } from "@mui/x-chat"
import type { ChatComposerAttachButtonProps, ChatComposerAttachmentListProps, ChatComposerTextAreaProps } from "@mui/x-chat"
import { useChatComposer } from "@mui/x-chat-headless"
import type { ChatDraftAttachment } from "@mui/x-chat-headless"
import { AttachmentChip, attachmentDetail } from "./attachment-chip"
import { INVALID_ATTACHMENT_FILENAME_MESSAGE, isAttachmentFilenameValid } from "./attachments-config"

const ATTACH_BUTTON_SX = {
  marginRight: "auto",
  padding: "5px",
  bgcolor: "var(--app-primary-filled)",
  color: "var(--mui-palette-primary-contrastText)",
  "&:hover": { bgcolor: "var(--mui-palette-primary-dark)" },
} satisfies SxProps<Theme>

const LIST_SX = { px: 1, pt: 1, gap: 0.5 } satisfies SxProps<Theme>

const STATUS_LABELS: Record<ChatDraftAttachment["status"], string> = {
  queued: "Waiting to upload",
  uploading: "Uploading",
  uploaded: "Ready to send",
  error: "Upload failed",
}

const INVALID_FILENAME_LABEL = "Invalid file name"

const REMOVE_BUTTON_SX = { flexShrink: 0 } satisfies SxProps<Theme>

function DraftChipIcon({ status }: { status: ChatDraftAttachment["status"] }) {
  if (status === "error") return <ErrorOutlined fontSize="small" color="error" />
  if (status === "uploaded") return <InsertDriveFileOutlined fontSize="small" color="action" />
  return <CircularProgress size={14} aria-hidden />
}

function DraftAttachmentChip({ attachment }: { attachment: ChatDraftAttachment }) {
  const { removeAttachment } = useChatComposer()
  const fileName = attachment.file.name || "Attachment"
  const hasUsableName = isAttachmentFilenameValid(attachment.file.name)
  const hasFailed = !hasUsableName || attachment.status === "error"
  const detail = !hasUsableName ? INVALID_FILENAME_LABEL : attachment.status === "error" ? STATUS_LABELS.error : attachmentDetail(fileName, attachment.file.type, attachment.file.size)
  return (
    <AttachmentChip
      data-testid="kelly-attachment-chip"
      data-status={attachment.status}
      icon={<DraftChipIcon status={attachment.status} />}
      name={fileName}
      detail={detail}
      detailTitle={hasUsableName ? undefined : INVALID_ATTACHMENT_FILENAME_MESSAGE}
      hasFailed={hasFailed}
      action={<>
        {!hasFailed && <Typography component="span" variant="caption" sx={visuallyHidden}>{STATUS_LABELS[attachment.status]}</Typography>}
        <IconButton size="small" sx={REMOVE_BUTTON_SX} aria-label={`Remove ${fileName}`} onClick={() => removeAttachment(attachment.localId)}>
          <Close fontSize="inherit" />
        </IconButton>
      </>}
    />
  )
}

export function KellyComposerAttachmentList(props: ChatComposerAttachmentListProps) {
  const { attachments } = useChatComposer()
  return (
    <ChatComposerAttachmentList {...props} sx={LIST_SX}>
      {attachments.map((attachment) => <DraftAttachmentChip key={attachment.localId} attachment={attachment} />)}
    </ChatComposerAttachmentList>
  )
}

export function KellyComposerTextArea({ placeholder, ...props }: ChatComposerTextAreaProps) {
  const { attachments } = useChatComposer()
  return <ChatComposerTextArea {...props} placeholder={attachments.length > 0 ? "" : placeholder} />
}

export function KellyComposerAttachButton(props: ChatComposerAttachButtonProps) {
  return (
    <Tooltip title="Attach files">
      <ChatComposerAttachButton {...props} sx={ATTACH_BUTTON_SX}>
        <AttachFile fontSize="small" />
      </ChatComposerAttachButton>
    </Tooltip>
  )
}
